import { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useApi from "./useApi";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  Divider,
  Box,
} from "@mui/material";
import {
  UploadFile as UploadFileIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { JsonEditor } from "./document_creator_assistant/DocumentProcessor";
import { PiBracketsCurly } from "react-icons/pi";
import { IoMdDoneAll } from "react-icons/io";


const ProcessEvaluation = () => {
  const [apiState, callApi] = useApi()
  const [targetPdf, setTargetPdf] = useState(null)
  const [generatedPdf, setGeneratedPdf] = useState(null)
  const [jsonFile, setJsonFile] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [status, setStatus] = useState(null)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(0)
  const [showRawJson, setShowRawJson] = useState(false)

  const handleFileChange = (setter) => (event) => {
    const file = event.target.files?.[0] || null
    setter(file)
    if (file) {
      toast.success(`${file.name} has been selected`)
    }
  }

  const handleSubmit = async () => {
    if (!targetPdf || !generatedPdf || !jsonFile) {
      toast.error("Please upload all three files before proceeding.")
      return
    }

    setProcessing(true)
    setStatus(null)
    setResult(null)
    setError(null)
    setProgress(0)

    const formData = new FormData()
    formData.append("target_pdf", targetPdf)
    formData.append("generated_pdf", generatedPdf)
    formData.append("input_json", jsonFile)

    try {
      const data = await callApi({
        url: "api/pdf-comparator",
        method: "POST",
        body: formData,
      })

      pollStatus(data.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred")
      setProcessing(false)
    }
  }

  const pollStatus = (jobId) => {
    let progressValue = 0
    const intervalId = setInterval(async () => {
      try {
        progressValue = Math.min(progressValue + Math.random() * 15, 90)
        setProgress(progressValue)

        const data = await callApi({
          url: `api/pdf-comparator/${jobId}/status`,
          method: "GET",
        })
        setStatus(data.status)

        if (data.status.toLowerCase() === "success") {
          clearInterval(intervalId)
          setProgress(100)
          fetchResults(jobId)
          setProcessing(false)
          toast.success("Processing completed successfully.")
        } else if (data.status.toLowerCase() === "failure") {
          clearInterval(intervalId)
          setError("Processing failed.")
          setProcessing(false)
          toast.error("There was an error processing your files.")
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An error occurred"
        setError(errorMessage)
        clearInterval(intervalId)
        setProcessing(false)
      }
    }, 3000)
  }

  const fetchResults = async (taskId) => {
    try {
      const data = await callApi({
        url: `api/pdf-comparator/${taskId}/result`,
        method: "GET",
      })
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch results")
    }
  }

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2))
      toast.success("Results copied to clipboard.")
    }
  }

  const downloadJson = () => {
    if (result) {
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "evaluation-results.json"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const getStatusIcon = () => {
    if (processing) return <HourglassEmptyIcon />
    if (error) return <ErrorIcon color="error" />
    if (result) return <CheckCircleIcon color="success" />
    return <ErrorIcon color="disabled" />
  }

  const getStatusColor = () => {
    if (processing) return "info"
    if (error) return "error"
    if (result) return "success"
    return "default"
  }

  const FileUploadCard = ({ title, description, file, onChange, accept, icon }) => (
    <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon || <UploadFileIcon color="primary" />}
            <Typography variant="h6" component="div" color="text.primary">
              {title}
            </Typography>
          </Box>
        }
        subheader={
          <Typography variant="body2" color="text.primary">
            {description}
          </Typography>
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Button component="label" variant="outlined" fullWidth>
          Upload File
          <input type="file" accept={accept} onChange={onChange} hidden />
        </Button>
        {file && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CheckCircleIcon sx={{ color: 'success.main' }} />
            <Typography variant="body2" noWrap color="text.primary">
              {file.name}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        mx: 'auto',
        my: 4,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom color="text.primary">
          PDF Formatting Agent
        </Typography>
        <Typography variant="subtitle1" color="text.primary">
          Upload your files to compare and edit PDF documents efficiently.
        </Typography>
      </Box>

      {/* File Upload Section */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        <FileUploadCard
          title="Target PDF"
          description="The reference PDF file"
          file={targetPdf}
          onChange={handleFileChange(setTargetPdf)}
          accept=".pdf"
          icon={<UploadFileIcon color="primary" />}
        />
        <FileUploadCard
          title="Generated PDF"
          description="The generated PDF to compare"
          file={generatedPdf}
          onChange={handleFileChange(setGeneratedPdf)}
          accept=".pdf"
          icon={<IoMdDoneAll color="#4caf50" />}
        />
        <FileUploadCard
          title="JSON Input"
          description="Configuration or input data"
          file={jsonFile}
          onChange={handleFileChange(setJsonFile)}
          accept=".json"
          icon={<PiBracketsCurly color="#1976d2" />}
        />
      </Box>

      {/* Action Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={handleSubmit}
          disabled={processing || !targetPdf || !generatedPdf || !jsonFile}
          variant="contained"
          size="large"
          sx={{ minWidth: 200 }}
          startIcon={processing ? <HourglassEmptyIcon /> : <UploadFileIcon />}
        >
          {processing ? 'Processing...' : 'Start Agent'}
        </Button>
      </Box>

      {/* Status Section */}
      {(status || processing || error) && (
        <Card>
          <CardHeader title="Processing Status" avatar={getStatusIcon()} />
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {processing && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Progress</Typography>
                  <Typography variant="body2">{`${Math.round(progress)}%`}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            )}

            {status && <Chip label={status} color={getStatusColor()} />}

            {error && (
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results Section */}
      {result && (
        <Card>
          <CardHeader
            title="Formatting Results"
            subheader="PDF formatting completed successfully"
            avatar={<CheckCircleIcon color="success" />}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowRawJson(!showRawJson)}
                  startIcon={showRawJson ? <VisibilityOffIcon /> : <VisibilityIcon />}
                >
                  {showRawJson ? 'Hide Raw JSON' : 'Show Raw JSON'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={copyToClipboard}
                  startIcon={<ContentCopyIcon />}
                >
                  Copy
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={downloadJson}
                  startIcon={<DownloadIcon />}
                >
                  Download
                </Button>
              </Box>
            }
            sx={{ textAlign: 'center' }} // Center the title and subheader
          />
          <CardContent>
            {showRawJson ? (
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  maxHeight: 500,
                  width: '100%',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  bgcolor: '#f8f9fa',
                  fontFamily: 'monospace',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#a8a8a8',
                    },
                  },
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: 'pre',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    color: '#2d3748',
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                  }}
                >
                  {JSON.stringify(result, null, 2)}
                </pre>
              </Box>
            ) : (
              <JsonEditor
                documentInfo={result}
                onUpdate={(updated) => setResult(updated)}
                onCopy={copyToClipboard}
                onSave={() => console.log('Save pressed')}
                onFinish={() => console.log('Finish pressed')}
                copyButtonText="Copy JSON"
                saveLoading={false}
                loading={false}
                isFinished={false}
                finishLoading={false}
              />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default ProcessEvaluation